import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import appointmentService from '../../services/appointmentService';
import queueService from '../../services/queueService';
import lookupService from '../../services/lookupService';
import CommonPagination from '../../components/common/CommonPagination';
import AppointmentBookingModal from './AppointmentBookingModal';
import AppointmentDayView from './AppointmentDayView';
import AppointmentReportPanel from './AppointmentReportPanel';
import { SettingsModal } from './AppointmentSettingsModal';
import { describeSlotError } from './SlotPicker';
import AppointmentDetailModal, { StatusPill } from './AppointmentDetailModal';
import { CancelModal, RescheduleModal } from './AppointmentActionModals';
import {
  APPOINTMENT_STATUSES, addDaysIso, ageLabel, appointmentTiming, buildRebookPrefill, canArrive, canCancel, canConfirm, canRebook,
  canRegister, canReschedule, formatDateLabel, registrationPathFor, todayIso,
} from '../../utils/appointmentLogic';
import {
  appointmentsToCsv, downloadTextFile, isReminderDue, openPrintWindow, printSlipInto, waitingMinutes, whatsAppLink,
} from '../../utils/appointmentExtras';
import './AppointmentPage.css';

export const AppointmentPage = () => {
  const { user, can } = useAuth();
  const navigate = useNavigate();

  const isAdmin = user?.roleName?.toUpperCase() === 'ADMINISTRATOR' || user?.roleId === 1;
  const perms = {
    create: can('APPOINTMENTS', 'CREATE'),
    update: can('APPOINTMENTS', 'UPDATE'),
    cancel: can('APPOINTMENTS', 'CANCEL'),
    print: can('APPOINTMENTS', 'PRINT'),
    export: can('APPOINTMENTS', 'EXPORT'),
    register: can('REGISTRATION', 'CREATE') || can('REGISTRATION', 'VIEW'),
    settings: can('APPOINTMENTS', 'UPDATE') && (isAdmin || user?.roleName?.toUpperCase() === 'CENTRE ADMIN'),
  };

  const [view, setView] = useState('list'); // 'list' | 'day' | 'report'
  const [from, setFrom] = useState(todayIso());
  const [to, setTo] = useState(todayIso());
  const [status, setStatus] = useState('');
  const [remindersDue, setRemindersDue] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [listError, setListError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const [skipped, setSkipped] = useState([]);

  const [lookups, setLookups] = useState(null);
  const [departments, setDepartments] = useState([]);

  const [booking, setBooking] = useState(false);
  const [bookingPrefill, setBookingPrefill] = useState(null);
  const [detail, setDetail] = useState(null);
  const [rescheduling, setRescheduling] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);

  // Clock for "starts in 40 min" / "slot ended 25 min ago"; refreshed every minute.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4500);
  };

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search.trim()); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [search]);

  // Lookups for the booking form: titles, genders, visit types, doctors and centres come from the same call Patient Registration uses.
  useEffect(() => {
    lookupService.getRegistrationLookups()
      .then((res) => { if (res.success) setLookups(res.data); })
      .catch(() => setLookups({}));
    appointmentService.departments()
      .then((res) => { if (res.success) setDepartments(res.data || []); })
      .catch(() => setDepartments([]));
  }, []);

  const listParams = useCallback(() => ({
    from: from || undefined, to: to || undefined, status: status || undefined,
    search: debouncedSearch || undefined, remindersDue: remindersDue || undefined,
  }), [from, to, status, debouncedSearch, remindersDue]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await appointmentService.list({ ...listParams(), page, pageSize });
      if (res.success && res.data) {
        setItems(res.data.items || []);
        setTotal(res.data.totalCount || 0);
        setCounts(res.data.statusCounts || {});
        setListError('');
      }
    } catch (err) {
      const message = describeSlotError(err);
      setListError(message === 'Could not load the slots.' ? 'Failed to load appointments.' : message);
    } finally {
      setLoading(false);
    }
  }, [listParams, page, pageSize]);

  useEffect(() => { if (view === 'list') fetchList(); }, [fetchList, view]);

  const refresh = () => { setReloadKey((k) => k + 1); if (view === 'list') fetchList(); };

  const allCount = useMemo(() => APPOINTMENT_STATUSES.reduce((n, s) => n + (counts[s.code] || 0), 0), [counts]);

  const setRange = (f, t) => { setFrom(f); setTo(t); setPage(1); };
  // Previous / next day: moves the whole range by one day.
  const shiftRange = (days) => { if (from) setFrom(addDaysIso(from, days)); if (to) setTo(addDaysIso(to, days)); setPage(1); };
  const initials = (name) => (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

  // One guarded runner for the simple actions so a double click cannot send the same change twice.
  const run = async (fn, okMessage) => {
    if (busyRef.current) return;
    busyRef.current = true; setBusy(true);
    try {
      const res = await fn();
      if (res.success) {
        showToast(okMessage);
        setDetail((d) => (d && res.data && d.appointmentId === res.data.appointmentId ? res.data : d));
        refresh();
      } else {
        showToast(res.message || 'The action failed.', 'error');
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'The action failed.', 'error');
      refresh();
    } finally { busyRef.current = false; setBusy(false); }
  };

  const confirmAppt = (a) => run(() => appointmentService.confirm(a.appointmentId, { expectedVersion: a.version }), `${a.appointmentNo} confirmed.`);
  const noShowAppt = (a) => run(() => appointmentService.noShow(a.appointmentId, { expectedVersion: a.version }), `${a.appointmentNo} marked as no-show.`);
  const registerAppt = (a) => navigate(registrationPathFor(a.appointmentId));

  // A centre that runs a registration counter gives the arrived patient a queue token too. It never blocks the registration:
  // a failure is shown, an existing token (409) is fine.
  const joinQueue = async (a) => {
    if (!can('QUEUE', 'CREATE')) return;
    try {
      const counters = await queueService.counters();
      if (!counters.success || !(counters.data || []).some((c) => c.stage === 'REGISTRATION')) return;
      const res = await queueService.issue({ stage: 'REGISTRATION', appointmentId: a.appointmentId });
      if (res.success) showToast(`Queue token ${res.data.tokenLabel} issued.`);
    } catch (err) {
      if (err.response?.status !== 409) showToast(err.response?.data?.message || 'The queue token could not be issued.', 'error');
    }
  };

  // The patient has walked in. The server confirms a BOOKED appointment and gives the queue token (one call), and only then
  // does the registration screen open with the data. If the registration is not completed the appointment stays CONFIRMED
  // (arrived, waiting) in this list.
  const arriveAndRegister = async (a) => {
    if (busyRef.current) return;
    busyRef.current = true; setBusy(true);
    try {
      if (canArrive(a)) {
        const res = await appointmentService.arrive(a.appointmentId, { expectedVersion: a.version });
        if (!res.success) {
          showToast(res.message || 'Could not mark the patient as arrived.', 'error');
          return;
        }
        await joinQueue(a);
      }
      navigate(registrationPathFor(a.appointmentId));
    } catch (err) {
      showToast(err.response?.data?.message || 'Could not mark the patient as arrived.', 'error');
      refresh();
    } finally { busyRef.current = false; setBusy(false); }
  };

  // Reminder by WhatsApp: opens a chat with the message filled in; the reminder is recorded only when the window opened.
  const sendReminder = async (a) => {
    const link = whatsAppLink(a);
    if (!link) { showToast('This mobile number cannot be used for WhatsApp.', 'error'); return; }
    // Not the 'noopener' feature: it makes window.open return null, which would look like a blocked pop-up. The opener is cut by hand.
    const w = window.open(link, '_blank');
    if (!w) { showToast('The browser blocked the pop-up. Allow pop-ups for this site and try again.', 'error'); return; }
    try { w.opener = null; } catch (err) { /* cross-origin windows may refuse; the chat page cannot reach this one anyway */ }
    run(() => appointmentService.reminder(a.appointmentId, 'WHATSAPP'), `Reminder recorded for ${a.appointmentNo}.`);
  };

  // The print window is opened straight from the click; the centre's slip instructions are fetched while it shows "Preparing".
  const printAppointmentSlip = async (a) => {
    const w = openPrintWindow();
    if (!w) { showToast('The browser blocked the pop-up. Allow pop-ups for this site and try again.', 'error'); return; }
    let instructions = '';
    try {
      const res = await appointmentService.getConfig(a.centreId);
      instructions = res.success ? res.data?.slipInstructions || '' : '';
    } catch (err) { /* the slip prints without instructions */ }
    printSlipInto(w, a, { centreName: a.centreName, instructions });
  };

  const exportCsv = async () => {
    try {
      const res = await appointmentService.exportRows(listParams());
      if (res.success) {
        downloadTextFile(`appointments-${from || 'all'}-to-${to || 'all'}.csv`, appointmentsToCsv(res.data || []));
        showToast(`${(res.data || []).length} appointment(s) exported.`);
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'The export failed.', 'error');
    }
  };

  const openBooking = (prefill = null) => { setBookingPrefill(prefill); setBooking(true); };
  const rebook = (a) => { setDetail(null); openBooking(buildRebookPrefill(a)); };

  const onBooked = (a) => {
    setBooking(false);
    showToast(`Appointment ${a.appointmentNo} booked for ${formatDateLabel(a.appointmentDate)}, ${a.slotStart}.`);
    // Show the day the appointment was booked for.
    const d = String(a.appointmentDate).split('T')[0];
    setStatus(''); setSearch(''); setRemindersDue(false); setRange(d, d);
    refresh();
  };

  const onBookedSeries = (result, message) => {
    setBooking(false);
    showToast(message || `${result.created.length} appointments booked.`);
    setSkipped(result.skipped || []);
    const first = result.created[0];
    if (first) {
      const d = String(first.appointmentDate).split('T')[0];
      setStatus(''); setSearch(''); setRemindersDue(false); setRange(d, addDaysIso(d, 28));
    }
    refresh();
  };

  const afterChange = (message) => (a) => {
    setRescheduling(null); setCancelling(null);
    setDetail((d) => (d ? a : d));
    showToast(message);
    refresh();
  };

  const dateToolbar = (
    <div className="apt-toolbar">
      <div className="apt-range">
        <div className="apt-daynav" role="group" aria-label="Change day">
          <button type="button" className="apt-btn apt-btn-sm" onClick={() => shiftRange(-1)} aria-label="Previous day">‹</button>
          <button type="button" className="apt-btn apt-btn-sm" onClick={() => shiftRange(1)} aria-label="Next day">›</button>
        </div>
        <button type="button" className="apt-btn apt-btn-sm" onClick={() => setRange(todayIso(), todayIso())}>Today</button>
        <button type="button" className="apt-btn apt-btn-sm" onClick={() => setRange(addDaysIso(todayIso(), 1), addDaysIso(todayIso(), 1))}>Tomorrow</button>
        {view === 'list' && <button type="button" className="apt-btn apt-btn-sm" onClick={() => setRange(todayIso(), addDaysIso(todayIso(), 6))}>Next 7 days</button>}
        <label className="apt-inline-label">{view === 'day' ? 'Day' : 'From'} <input type="date" className="apt-input apt-input-sm" value={from} onChange={(e) => { setFrom(e.target.value); if (view === 'day') setTo(e.target.value); setPage(1); }} /></label>
        {view === 'list' && <label className="apt-inline-label">To <input type="date" className="apt-input apt-input-sm" value={to} min={from || undefined} onChange={(e) => { setTo(e.target.value); setPage(1); }} /></label>}
      </div>
      {view === 'list' && <input className="apt-input apt-search" placeholder="Search name, mobile, appointment no or UHID" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search appointments" />}
    </div>
  );

  return (
    <div className="apt-page">
      {toast && <div className={`apt-toast ${toast.type === 'error' ? 'error' : ''}`} role="alert">{toast.message}</div>}

      <div className="apt-header">
        <div className="apt-title-wrap">
          <span className="apt-title-icon" aria-hidden="true">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
            </svg>
          </span>
          <div>
            <h1>Appointments</h1>
            <p className="apt-subtitle">Book and manage visits before registration. A confirmed appointment is registered from here; walk-ins go straight to registration.</p>
          </div>
        </div>
        <div className="apt-header-actions">
          {perms.settings && <button type="button" className="apt-btn" onClick={() => setSettingsOpen(true)}>Settings</button>}
          <Link className="apt-btn" to="/registration">New walk-in</Link>
          {perms.create && <button type="button" className="apt-btn apt-btn-primary" onClick={() => openBooking()}>+ New appointment</button>}
        </div>
      </div>

      <div className="apt-views" role="tablist" aria-label="Appointment views">
        {[['list', 'List'], ['day', 'Day view'], ['report', 'Reports']].map(([key, label]) => (
          <button key={key} type="button" role="tab" aria-selected={view === key} className={view === key ? 'active' : ''} onClick={() => setView(key)}>{label}</button>
        ))}
      </div>

      {skipped.length > 0 && (
        <div className="apt-state apt-state-closed apt-state-banner" role="status">
          <p><strong>{skipped.length} date(s) were skipped:</strong> {skipped.map((s) => `${formatDateLabel(s.date)} (${s.reason})`).join('; ')}</p>
          <button type="button" className="apt-btn apt-btn-sm" onClick={() => setSkipped([])}>Dismiss</button>
        </div>
      )}

      {view === 'report' && <AppointmentReportPanel centres={lookups?.centres} isAdmin={isAdmin} canExport={perms.export} />}

      {view === 'day' && (
        <>
          {dateToolbar}
          <AppointmentDayView date={from} canBook={perms.create} refreshKey={reloadKey} onOpen={setDetail}
            onBookSlot={(date, slotStart) => openBooking({ form: { appointmentDate: date, slotStart } })} />
        </>
      )}

      {view === 'list' && (
        <>
          {dateToolbar}

          <div className="apt-kpis" role="tablist" aria-label="Filter by status">
            <button type="button" role="tab" aria-selected={status === ''} className={`apt-kpi apt-kpi-all ${status === '' ? 'active' : ''}`} onClick={() => { setStatus(''); setPage(1); }}>
              <span className="apt-kpi-count">{allCount}</span>
              <span className="apt-kpi-label">All</span>
            </button>
            {APPOINTMENT_STATUSES.map((s) => (
              <button key={s.code} type="button" role="tab" aria-selected={status === s.code}
                className={`apt-kpi apt-kpi-${s.tone} ${status === s.code ? 'active' : ''}`} onClick={() => { setStatus(s.code); setPage(1); }}>
                <span className="apt-kpi-count">{counts[s.code] || 0}</span>
                <span className="apt-kpi-label">{s.label}</span>
              </button>
            ))}
          </div>

          <div className="apt-tools" style={{ marginBottom: 12 }}>
            <button type="button" className={`apt-btn apt-btn-sm ${remindersDue ? 'apt-btn-primary' : ''}`} aria-pressed={remindersDue}
              onClick={() => { setRemindersDue((v) => !v); setPage(1); if (!remindersDue) setRange(todayIso(), addDaysIso(todayIso(), 1)); }}
              title="Open appointments of today and tomorrow that have not been reminded yet">Reminders due</button>
            {perms.export && <button type="button" className="apt-btn apt-btn-sm" onClick={exportCsv}>Export CSV</button>}
          </div>

          {listError && (
            <div className="apt-state apt-state-error apt-state-banner" role="alert">
              <p>{listError}</p>
              <button type="button" className="apt-btn apt-btn-sm" onClick={fetchList}>Try again</button>
            </div>
          )}

          <div className="apt-table-wrap">
            <table className="apt-table">
              <thead>
                <tr>
                  <th>Appointment</th><th>Date and slot</th><th>Patient</th><th>Mobile</th><th>Department / Visit</th><th>Status</th><th className="apt-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && items.length === 0 && Array.from({ length: 5 }).map((_, i) => (
                  <tr key={`sk${i}`} aria-hidden="true">
                    {Array.from({ length: 7 }).map((__, j) => <td key={j}><span className="apt-skeleton" /></td>)}
                  </tr>
                ))}
                {!loading && items.length === 0 && !listError && (
                  <tr>
                    <td colSpan={7} className="apt-empty">
                      <div className="apt-empty-art" aria-hidden="true">
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
                        </svg>
                      </div>
                      <strong>No appointments for this selection.</strong>
                      <div className="apt-muted">Try another day or status{perms.create ? ', or book a new appointment.' : '.'}</div>
                      {perms.create && <button type="button" className="apt-btn apt-btn-primary apt-btn-sm" style={{ marginTop: 10 }} onClick={() => openBooking()}>+ New appointment</button>}
                    </td>
                  </tr>
                )}
                {items.map((a) => {
                  const timing = appointmentTiming(a, now);
                  const waiting = a.isWaiting ? waitingMinutes(a, now) : null;
                  return (
                    <tr key={a.appointmentId} className={timing?.state === 'overdue' ? 'apt-row-overdue' : ''}>
                      <td>
                        <button type="button" className="apt-link" onClick={() => setDetail(a)}>{a.appointmentNo}</button>
                        {a.tokenNo ? <span className="apt-token" title="Queue token">#{a.tokenNo}</span> : null}
                      </td>
                      <td>
                        <span className="apt-slot-badge">{a.slotStart}</span>
                        <div className="apt-muted">{formatDateLabel(a.appointmentDate)} · till {a.slotEnd}</div>
                        {timing && timing.label && <span className={`apt-timing apt-timing-${timing.state}`}>{timing.label}</span>}
                      </td>
                      <td>
                        <div className="apt-person">
                          <span className="apt-avatar" aria-hidden="true">{initials(a.pName)}</span>
                          <div>
                            <strong>{`${a.title || ''} ${a.pName}`.trim()}</strong>
                            <div className="apt-muted">{[a.gender, ageLabel(a)].filter(Boolean).join(' · ')} {a.patientId ? <span className="apt-uhid">{a.patientId}</span> : <span className="apt-new-tag">New patient</span>}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        {a.mobile}
                        {a.reminderCount > 0 && <span className="apt-reminded">Reminded {a.reminderCount}x</span>}
                      </td>
                      <td>{a.departmentName || '-'}<div className="apt-muted">{a.visitType}</div></td>
                      <td>
                        <StatusPill status={a.status} />
                        {waiting !== null && <div><span className="apt-waiting">Waiting {waiting} min</span></div>}
                      </td>
                      <td className="apt-right">
                        <div className="apt-actions">
                          <button type="button" className="apt-btn apt-btn-sm" onClick={() => setDetail(a)}>View</button>
                          {canArrive(a) && perms.update && perms.register && (
                            <button type="button" className="apt-btn apt-btn-sm apt-btn-primary" disabled={busy} onClick={() => arriveAndRegister(a)}
                              title="The patient has arrived: confirm, give a token and open registration with this data">Arrived · Register</button>
                          )}
                          {canConfirm(a) && perms.update && <button type="button" className="apt-btn apt-btn-sm" disabled={busy} onClick={() => confirmAppt(a)}>Confirm</button>}
                          {canRegister(a) && !canArrive(a) && perms.register && <button type="button" className="apt-btn apt-btn-sm apt-btn-primary" onClick={() => registerAppt(a)}>Register</button>}
                          {isReminderDue(a, now) && perms.update && whatsAppLink(a) && <button type="button" className="apt-btn apt-btn-sm" disabled={busy} onClick={() => sendReminder(a)} title="Open WhatsApp with the reminder filled in">Remind</button>}
                          {canReschedule(a) && perms.update && <button type="button" className="apt-btn apt-btn-sm" onClick={() => setRescheduling(a)}>Reschedule</button>}
                          {timing?.state === 'overdue' && perms.update && <button type="button" className="apt-btn apt-btn-sm" disabled={busy} onClick={() => noShowAppt(a)} title="The patient did not come">No-show</button>}
                          {canCancel(a) && perms.cancel && <button type="button" className="apt-btn apt-btn-sm apt-btn-danger" onClick={() => setCancelling(a)}>Cancel</button>}
                          {canRebook(a) && perms.create && <button type="button" className="apt-btn apt-btn-sm" onClick={() => rebook(a)} title="Book this patient again">Rebook</button>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <CommonPagination page={page} pageSize={pageSize} totalRecords={total} loading={loading}
            onPageChange={setPage} onPageSizeChange={(n) => { setPageSize(n); setPage(1); }} />
        </>
      )}

      <AppointmentBookingModal isOpen={booking} onClose={() => setBooking(false)} onBooked={onBooked} onBookedSeries={onBookedSeries}
        prefill={bookingPrefill} lookups={lookups} departments={departments} defaultCentreId={user?.centreId}
        defaultDate={from >= todayIso() ? from : todayIso()} isAdmin={isAdmin} />

      {detail && (
        <AppointmentDetailModal appointment={detail} perms={perms} busy={busy} onClose={() => setDetail(null)}
          onConfirm={confirmAppt} onNoShow={noShowAppt} onRegister={registerAppt} onArrive={arriveAndRegister}
          onReschedule={(a) => setRescheduling(a)} onCancel={(a) => setCancelling(a)}
          onPrint={printAppointmentSlip} onReminder={sendReminder} onRebook={rebook} />
      )}
      {rescheduling && <RescheduleModal appointment={rescheduling} onClose={() => setRescheduling(null)} onDone={afterChange('Appointment rescheduled.')} />}
      {cancelling && <CancelModal appointment={cancelling} onClose={() => setCancelling(null)} onDone={afterChange('Appointment cancelled.')} />}
      {settingsOpen && (
        <SettingsModal centreId={user?.centreId} centres={lookups?.centres} departments={departments} isAdmin={isAdmin}
          onClose={() => setSettingsOpen(false)} onSaved={() => { setSettingsOpen(false); showToast('Appointment settings saved.'); }} />
      )}
    </div>
  );
};

export default AppointmentPage;
